import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { NotificationHistory } from "../models/notification-history.model";
import { formatLocalizedDateTime } from "common/helpers/dateUtils";

interface NotificationDetailsModalProps {
  notification?: NotificationHistory;
  show: boolean;
  onClose: () => void;
}

export default function NotificationDetailsModal({ notification, show, onClose }: NotificationDetailsModalProps) {
  const { t } = useTranslation();

  const getTranslatedType = (type: string) => {
    const typeTranslations: Record<string, string> = {
      'agentDisconnection': t('notificationHistory.types.agentDisconnection'),
      'stepExecution': t('notificationHistory.types.stepExecution'),
      'unexecutedSchedules': t('notificationHistory.types.unexecutedSchedules')
    };
    return typeTranslations[type] || type;
  };

  const getTranslatedStatus = (statusId: number) => {
    switch (statusId) {
      case 0: return t('notificationHistory.status.pending');
      case 1: return t('notificationHistory.status.sent');
      case 2: return t('notificationHistory.status.failed');
      default: return '';
    }
  };

  const getTranslatedChannel = (channelId: number) => {
    switch (channelId) {
      case 1: return 'App';
      case 2: return 'Email';
      case 3: return 'Teams';
      default: return 'Unknown';
    }
  };

  const renderCustomContent = (custom: string) => {
    try {
      const parsed = JSON.parse(custom);
      return (
        <pre className="mb-0 fs-7" style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
          {JSON.stringify(parsed, null, 2)}
        </pre>
      );
    } catch {
      return (
        <pre className="mb-0 fs-7" style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
          {custom}
        </pre>
      );
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <div className="card">
        <div className="card-body">
          <div className="mb-7">
            <h3 className="fs-2x fw-bold">{t('notificationHistory.modal.title')}</h3>
          </div>

          {notification && (
            <div className="row">
              <div className="col-md-6 mb-4">
                <label className="form-label fw-bold fs-6 text-gray-600">{t('notificationHistory.modal.user')}:</label>
                <div className="form-control form-control-solid bg-light fs-6">
                  {notification.userName}
                </div>
              </div>

              <div className="col-md-6 mb-4">
                <label className="form-label fw-bold fs-6 text-gray-600">{t('notificationHistory.modal.channel')}:</label>
                <div className="form-control form-control-solid bg-light fs-6">
                  {getTranslatedChannel(notification.notificationChannelId)}
                </div>
              </div>

              <div className="col-md-6 mb-4">
                <label className="form-label fw-bold fs-6 text-gray-600">{t('notificationHistory.modal.type')}:</label>
                <div className="form-control form-control-solid bg-light fs-6">
                  {getTranslatedType(notification.type)}
                </div>
              </div>

              <div className="col-md-6 mb-4">
                <label className="form-label fw-bold fs-6 text-gray-600">{t('notificationHistory.modal.status')}:</label>
                <div className="form-control form-control-solid bg-light fs-6">
                  {getTranslatedStatus(notification.statusId)}
                </div>
              </div>

              <div className="col-md-6 mb-4">
                <label className="form-label fw-bold fs-6 text-gray-600">{t('notificationHistory.modal.date')}:</label>
                <div className="form-control form-control-solid bg-light fs-6">
                  {formatLocalizedDateTime(new Date(notification.when).toISOString(), 'llll')}
                </div>
              </div>

              {notification.custom && (
                <div className="col-12 mb-4">
                  <label className="form-label fw-bold fs-6 text-gray-600">{t('notificationHistory.modal.details')}:</label>
                  <div className="form-control form-control-solid bg-light fs-7" style={{ minHeight: '200px', overflow: 'auto' }}>
                    {renderCustomContent(notification.custom)}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="card-footer">
          <div className="row justify-content-end">
            <div className="col-md-2">
              <button type="button" className="btn btn-primary" onClick={onClose}>
                {t('notificationHistory.modal.close')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}